/**
 * MobileBookingWizard.js - Step-by-step mobile booking flow
 * Provides app-like booking experience on mobile devices
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronLeft, ChevronRight, Check, MapPin, Calendar, Clock, 
  CreditCard, User, Phone, Mail, FileText, Sparkles, X,
  Home, Building, Package, Truck, Shirt, CheckCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

const serviceIcons = {
  cleaning: Home,
  laundry: Shirt,
  courier: Truck,
  errands: Package,
  tasks: Sparkles
};

const MobileBookingWizard = ({ 
  service, 
  onComplete, 
  onCancel,
  initialData = {},
  isGuest = false,  // If true, user is not logged in - no cash option
  user = null       // Current logged in user
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    // Step 1: Service confirmed
    serviceId: service?.id || '',
    serviceName: service?.name || '',
    serviceType: service?.category || 'cleaning',
    
    // Step 2: Service details
    specialRequests: '',
    urgency: 'normal',
    
    // Step 3: Address
    address: '',
    apartment: '',
    city: '',
    state: 'NC',
    zipCode: '',
    
    // Step 4: Date & Time
    scheduledDate: '',
    scheduledTime: '',
    
    // Step 5: Add-ons
    selectedAddons: [],
    
    // Step 6: Contact info (if guest)
    clientName: '',
    clientPhone: '',
    clientEmail: '',
    
    // Step 7: Payment method
    paymentMethod: 'card',
    
    ...initialData
  });
  
  // Pricing
  const [pricing, setPricing] = useState({
    baseAmount: service?.base_price || service?.price_per_hour || 50,
    addonsAmount: 0,
    totalAmount: service?.base_price || service?.price_per_hour || 50
  });
  
  const totalSteps = 8;
  
  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const calculatePricing = () => {
    const base = service?.base_price || service?.price_per_hour || 50;
    const addonsTotal = formData.selectedAddons.reduce((sum, addon) => sum + (addon.price || 0), 0);
    setPricing({
      baseAmount: base,
      addonsAmount: addonsTotal,
      totalAmount: base + addonsTotal
    });
  };
  
  useEffect(() => {
    calculatePricing();
  }, [formData.selectedAddons]);
  
  const validateStep = () => {
    switch (currentStep) {
      case 1: // Service
        return true;
      case 2: // Details
        return true;
      case 3: // Address
        return formData.address && formData.city && formData.zipCode;
      case 4: // Date/Time
        return formData.scheduledDate && formData.scheduledTime;
      case 5: // Add-ons
        return true;
      case 6: // Contact
        return formData.clientName && formData.clientEmail && formData.clientPhone;
      case 7: // Payment
        return formData.paymentMethod;
      case 8: // Review
        return true;
      default:
        return true;
    }
  };
  
  const nextStep = () => {
    if (validateStep()) {
      if (currentStep < totalSteps) {
        setCurrentStep(prev => prev + 1);
      }
    } else {
      toast.error('Please fill in all required fields');
    }
  };
  
  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };
  
  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onComplete({
        ...formData,
        ...pricing
      });
    } catch (error) {
      toast.error('Failed to create booking');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const ServiceIcon = serviceIcons[formData.serviceType] || Sparkles;
  
  // Progress indicator
  const ProgressBar = () => (
    <div className="px-4 py-3 bg-[var(--background-paper)] border-b border-[var(--border-primary)]">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-[var(--text-muted)]">Step {currentStep} of {totalSteps}</span>
        <span className="text-sm font-medium text-[var(--primary)]">
          {Math.round((currentStep / totalSteps) * 100)}%
        </span>
      </div>
      <div className="h-1.5 bg-[var(--background-subtle)] rounded-full overflow-hidden">
        <motion.div 
          className="h-full bg-[var(--primary)] rounded-full"
          initial={{ width: 0 }}
          animate={{ width: `${(currentStep / totalSteps) * 100}%` }}
          transition={{ duration: 0.3 }}
        />
      </div>
    </div>
  );
  
  // Step content renderer
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <StepContainer title="Confirm Service" icon={<ServiceIcon className="w-6 h-6" />}>
            <div className="bg-[var(--background-subtle)] rounded-2xl p-4 border border-[var(--border-gold)]">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-xl bg-[var(--primary)]/20 flex items-center justify-center">
                  <ServiceIcon className="w-8 h-8 text-[var(--primary)]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold">{service?.name}</h3>
                  <p className="text-[var(--text-muted)] text-sm">{service?.category}</p>
                  <p className="text-[var(--primary)] font-bold mt-1">
                    ${pricing.baseAmount.toFixed(2)}
                  </p>
                </div>
              </div>
              {service?.description && (
                <p className="mt-4 text-sm text-[var(--text-secondary)]">
                  {service.description}
                </p>
              )}
            </div>
          </StepContainer>
        );
        
      case 2:
        return (
          <StepContainer title="Service Details" icon={<FileText className="w-6 h-6" />}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Special Requests</label>
                <Textarea
                  value={formData.specialRequests}
                  onChange={(e) => updateFormData('specialRequests', e.target.value)}
                  placeholder="Any special instructions or requirements..."
                  className="min-h-[120px] bg-[var(--background-subtle)] border-[var(--border-primary)]"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Urgency</label>
                <div className="grid grid-cols-3 gap-2">
                  {['normal', 'priority', 'urgent'].map((level) => (
                    <button
                      key={level}
                      type="button"
                      onClick={() => updateFormData('urgency', level)}
                      className={`py-3 px-4 rounded-xl text-sm font-medium capitalize transition-all ${
                        formData.urgency === level
                          ? 'bg-[var(--primary)] text-black'
                          : 'bg-[var(--background-subtle)] border border-[var(--border-primary)]'
                      }`}
                    >
                      {level}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </StepContainer>
        );
        
      case 3:
        return (
          <StepContainer title="Service Address" icon={<MapPin className="w-6 h-6" />}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Street Address *</label>
                <Input
                  value={formData.address}
                  onChange={(e) => updateFormData('address', e.target.value)}
                  placeholder="123 Main Street"
                  className="h-12 bg-[var(--background-subtle)]"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Apt/Suite/Unit</label>
                <Input
                  value={formData.apartment}
                  onChange={(e) => updateFormData('apartment', e.target.value)}
                  placeholder="Apt 4B"
                  className="h-12 bg-[var(--background-subtle)]"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-2">City *</label>
                  <Input
                    value={formData.city}
                    onChange={(e) => updateFormData('city', e.target.value)}
                    placeholder="Raleigh"
                    className="h-12 bg-[var(--background-subtle)]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">State</label>
                  <Input
                    value={formData.state}
                    onChange={(e) => updateFormData('state', e.target.value)}
                    placeholder="NC"
                    className="h-12 bg-[var(--background-subtle)]"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">ZIP Code *</label>
                <Input
                  value={formData.zipCode}
                  onChange={(e) => updateFormData('zipCode', e.target.value)}
                  placeholder="27601"
                  inputMode="numeric"
                  className="h-12 bg-[var(--background-subtle)]"
                />
              </div>
            </div>
          </StepContainer>
        );
        
      case 4:
        return (
          <StepContainer title="Date & Time" icon={<Calendar className="w-6 h-6" />}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Preferred Date *</label>
                <Input
                  type="date"
                  value={formData.scheduledDate}
                  onChange={(e) => updateFormData('scheduledDate', e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="h-12 bg-[var(--background-subtle)]"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Preferred Time *</label>
                <div className="grid grid-cols-3 gap-2">
                  {['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00'].map((time) => (
                    <button
                      key={time}
                      type="button"
                      onClick={() => updateFormData('scheduledTime', time)}
                      className={`py-3 px-2 rounded-xl text-sm font-medium transition-all ${
                        formData.scheduledTime === time
                          ? 'bg-[var(--primary)] text-black'
                          : 'bg-[var(--background-subtle)] border border-[var(--border-primary)]'
                      }`}
                    >
                      {time.split(':')[0] > 12 
                        ? `${time.split(':')[0] - 12}:${time.split(':')[1]} PM`
                        : `${time} AM`
                      }
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </StepContainer>
        );
        
      case 5:
        return (
          <StepContainer title="Add-ons" icon={<Sparkles className="w-6 h-6" />}>
            <div className="space-y-3">
              {(service?.addons || []).length > 0 ? (
                service.addons.map((addon) => {
                  const isSelected = formData.selectedAddons.some(a => a.id === addon.id);
                  return (
                    <button
                      key={addon.id}
                      type="button"
                      onClick={() => {
                        if (isSelected) {
                          updateFormData('selectedAddons', formData.selectedAddons.filter(a => a.id !== addon.id));
                        } else {
                          updateFormData('selectedAddons', [...formData.selectedAddons, addon]);
                        }
                      }}
                      className={`w-full p-4 rounded-xl text-left flex items-center justify-between transition-all ${
                        isSelected
                          ? 'bg-[var(--primary)]/20 border-2 border-[var(--primary)]'
                          : 'bg-[var(--background-subtle)] border border-[var(--border-primary)]'
                      }`}
                    >
                      <div>
                        <p className="font-medium">{addon.name}</p>
                        <p className="text-sm text-[var(--text-muted)]">{addon.description}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-[var(--primary)]">+${addon.price}</span>
                        {isSelected && <Check className="w-5 h-5 text-[var(--primary)]" />}
                      </div>
                    </button>
                  );
                })
              ) : (
                <div className="text-center py-8 text-[var(--text-muted)]">
                  <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No add-ons available for this service</p>
                </div>
              )}
            </div>
          </StepContainer>
        );
        
      case 6:
        return (
          <StepContainer title="Contact Information" icon={<User className="w-6 h-6" />}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Your Name *</label>
                <Input
                  value={formData.clientName}
                  onChange={(e) => updateFormData('clientName', e.target.value)}
                  placeholder="John Doe"
                  className="h-12 bg-[var(--background-subtle)]"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Email *</label>
                <Input
                  type="email"
                  value={formData.clientEmail}
                  onChange={(e) => updateFormData('clientEmail', e.target.value)}
                  placeholder="john@example.com"
                  inputMode="email"
                  className="h-12 bg-[var(--background-subtle)]"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Phone *</label>
                <Input
                  type="tel"
                  value={formData.clientPhone}
                  onChange={(e) => updateFormData('clientPhone', e.target.value)}
                  placeholder="(919) 555-0123"
                  inputMode="tel"
                  className="h-12 bg-[var(--background-subtle)]"
                />
              </div>
            </div>
          </StepContainer>
        );
        
      case 7:
        // Payment options - guests can only pay with card (no cash)
        const paymentOptions = isGuest 
          ? [{ id: 'card', label: 'Credit/Debit Card', icon: CreditCard }]
          : [
              { id: 'card', label: 'Credit/Debit Card', icon: CreditCard },
              { id: 'cash', label: 'Pay with Cash', icon: Building }
            ];
        
        return (
          <StepContainer title="Payment Method" icon={<CreditCard className="w-6 h-6" />}>
            <div className="space-y-3">
              {isGuest && (
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 mb-4">
                  <p className="text-sm text-amber-400">
                    <strong>Guest checkout:</strong> Online payment required. Create an account for cash payment option.
                  </p>
                </div>
              )}
              {paymentOptions.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => updateFormData('paymentMethod', id)}
                  className={`w-full p-4 rounded-xl text-left flex items-center gap-4 transition-all ${
                    formData.paymentMethod === id
                      ? 'bg-[var(--primary)]/20 border-2 border-[var(--primary)]'
                      : 'bg-[var(--background-subtle)] border border-[var(--border-primary)]'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    formData.paymentMethod === id ? 'bg-[var(--primary)]' : 'bg-[var(--background)]'
                  }`}>
                    <Icon className={`w-6 h-6 ${formData.paymentMethod === id ? 'text-black' : 'text-[var(--text-muted)]'}`} />
                  </div>
                  <span className="font-medium">{label}</span>
                  {formData.paymentMethod === id && (
                    <Check className="w-5 h-5 text-[var(--primary)] ml-auto" />
                  )}
                </button>
              ))}
            </div>
          </StepContainer>
        );
        
      case 8:
        return (
          <StepContainer title="Review & Confirm" icon={<CheckCircle className="w-6 h-6" />}>
            <div className="space-y-4">
              {/* Service Summary */}
              <div className="bg-[var(--background-subtle)] rounded-xl p-4">
                <h4 className="text-sm font-medium text-[var(--text-muted)] mb-2">Service</h4>
                <p className="font-semibold">{service?.name}</p>
              </div>
              
              {/* Address */}
              <div className="bg-[var(--background-subtle)] rounded-xl p-4">
                <h4 className="text-sm font-medium text-[var(--text-muted)] mb-2">Location</h4>
                <p className="font-medium">{formData.address}</p>
                <p className="text-sm text-[var(--text-secondary)]">
                  {formData.city}, {formData.state} {formData.zipCode}
                </p>
              </div>
              
              {/* Date/Time */}
              <div className="bg-[var(--background-subtle)] rounded-xl p-4">
                <h4 className="text-sm font-medium text-[var(--text-muted)] mb-2">When</h4>
                <p className="font-medium">
                  {formData.scheduledDate && new Date(formData.scheduledDate).toLocaleDateString('en-US', { 
                    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
                  })}
                </p>
                <p className="text-sm text-[var(--text-secondary)]">{formData.scheduledTime}</p>
              </div>
              
              {/* Pricing */}
              <div className="bg-[var(--background)] rounded-xl p-4 border border-[var(--border-gold)]">
                <div className="flex justify-between mb-2">
                  <span className="text-[var(--text-muted)]">Base Price</span>
                  <span>${pricing.baseAmount.toFixed(2)}</span>
                </div>
                {pricing.addonsAmount > 0 && (
                  <div className="flex justify-between mb-2">
                    <span className="text-[var(--text-muted)]">Add-ons</span>
                    <span>${pricing.addonsAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="h-px bg-[var(--border-primary)] my-3" />
                <div className="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="text-[var(--primary)]">${pricing.totalAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </StepContainer>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <div className="fixed inset-0 bg-[var(--background)] z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-[var(--background-paper)] border-b border-[var(--border-primary)]">
        <button
          onClick={onCancel}
          className="p-2 -ml-2 rounded-full hover:bg-[var(--background-subtle)]"
        >
          <X className="w-6 h-6" />
        </button>
        <h2 className="text-lg font-semibold font-['Cormorant_Garamond']">Book Service</h2>
        <div className="w-10" /> {/* Spacer */}
      </div>
      
      {/* Progress */}
      <ProgressBar />
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>
      
      {/* Footer Navigation */}
      <div className="px-4 py-4 bg-[var(--background-paper)] border-t border-[var(--border-primary)] safe-area-pb">
        <div className="flex gap-3">
          {currentStep > 1 && (
            <Button
              variant="outline"
              onClick={prevStep}
              className="flex-1 h-14 text-base"
            >
              <ChevronLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
          )}
          
          {currentStep < totalSteps ? (
            <Button
              onClick={nextStep}
              disabled={!validateStep()}
              className="flex-1 h-14 text-base bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-black"
            >
              Continue
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex-1 h-14 text-base bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-black"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  Processing...
                </span>
              ) : (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  Confirm Booking
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// Step container component
const StepContainer = ({ title, icon, children }) => (
  <div className="p-4">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-full bg-[var(--primary)]/20 flex items-center justify-center text-[var(--primary)]">
        {icon}
      </div>
      <h3 className="text-xl font-semibold font-['Cormorant_Garamond']">{title}</h3>
    </div>
    {children}
  </div>
);

export default MobileBookingWizard;
